import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Award } from "lucide-react";
import { loyaltyApi, loyaltyQueryKeys } from "../../../shared/api/loyalty";
import { apiError } from "../../../shared/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const kindLabel: Record<string, string> = {
  EARN: "Cộng điểm",
  REDEEM: "Trừ điểm",
  ADJUST: "Điều chỉnh",
};

export function PatientLoyaltySection({ patientId }: { patientId: string | undefined }) {
  const qc = useQueryClient();
  const pid = patientId ?? "";
  const acct = useQuery({
    queryKey: loyaltyQueryKeys.account(pid),
    queryFn: () => loyaltyApi.account(pid),
    enabled: !!patientId,
  });
  const txns = useQuery({
    queryKey: loyaltyQueryKeys.txns(pid),
    queryFn: () => loyaltyApi.transactions(pid),
    enabled: !!patientId,
  });

  const [points, setPoints] = useState(100);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: loyaltyQueryKeys.account(pid) });
    qc.invalidateQueries({ queryKey: loyaltyQueryKeys.txns(pid) });
  };

  const mut = (fn: () => Promise<void>, okMsg: string) => {
    setError(null);
    setMsg(null);
    fn()
      .then(() => {
        invalidate();
        setMsg(okMsg);
        setReason("");
      })
      .catch((e) => setError(apiError(e).detail));
  };

  const earn = useMutation({ mutationFn: () => loyaltyApi.earn(pid, points, reason) });
  const redeem = useMutation({ mutationFn: () => loyaltyApi.redeem(pid, points, reason) });

  if (!patientId) return null;
  const a = acct.data;

  return (
    <div className="grid gap-4">
      <Card>
        <CardContent className="p-4">
          <h3 className="mb-2 text-sm font-semibold">Thẻ thành viên</h3>
          {acct.isLoading ? (
            <p className="text-sm text-muted-foreground">Đang tải…</p>
          ) : (
            <div className="flex flex-wrap gap-8">
              <div>
                <div className="text-xs text-muted-foreground">Hạng</div>
                <div className="flex items-center gap-1.5 text-xl font-semibold">
                  <Award className="text-primary" aria-hidden="true" /> {a?.tier}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Điểm hiện có</div>
                <div className="text-xl font-semibold">{a?.pointsBalance ?? 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Điểm tích luỹ</div>
                <div className="text-xl font-semibold">{a?.lifetimePoints ?? 0}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="grid gap-2.5 p-4">
          <h3 className="text-sm font-semibold">Cộng / trừ điểm</h3>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="loyalty-points">Số điểm</Label>
              <Input
                id="loyalty-points"
                type="number"
                min={1}
                value={points}
                onChange={(e) => setPoints(Number.parseInt(e.target.value || "0", 10))}
                className="w-28"
              />
            </div>
            <div className="min-w-[200px] flex-1 space-y-1.5">
              <Label htmlFor="loyalty-reason">Lý do</Label>
              <Input
                id="loyalty-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Lý do (VD: hoá đơn 500k / đổi quà)"
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              disabled={points < 1 || earn.isPending}
              onClick={() => mut(() => loyaltyApi.earn(pid, points, reason), "Đã cộng điểm.")}
            >
              + Cộng
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={points < 1 || redeem.isPending}
              onClick={() => mut(() => loyaltyApi.redeem(pid, points, reason), "Đã trừ điểm.")}
            >
              − Trừ / Đổi
            </Button>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          {msg && <p className="text-sm text-emerald-600">{msg}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="mb-2 text-sm font-semibold">Lịch sử điểm</h3>
          {txns.isLoading ? (
            <p className="text-sm text-muted-foreground">Đang tải…</p>
          ) : (txns.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Chưa có giao dịch điểm.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {txns.data!.map((t, i) => (
                <div key={i} className="rounded-lg border bg-card p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-semibold text-foreground">
                      {kindLabel[t.kind] ?? t.kind}
                    </span>
                    <span
                      className={`text-base font-semibold ${t.points < 0 ? "text-destructive" : "text-emerald-600"}`}
                    >
                      {t.points > 0 ? `+${t.points}` : t.points}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    Thời gian: {new Date(t.at).toLocaleString("vi-VN")}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Lý do: {t.reason ?? "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
